import React, { useState } from 'react';
import { Mail, CheckCircle } from 'lucide-react';

const SubscriptionForm = ({ onAddSubscriber }) => {
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubscribing) return;
    setError('');

    const trimmed = email.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!trimmed || !emailRegex.test(trimmed)) {
      setError('Please enter a valid email address, like name@example.com.');
      return;
    }

    setIsSubscribing(true);

    try {
      const result = await onAddSubscriber(trimmed);

      if (result.success) {
        // Stays confirmed: reverting to the form invites a second submit
        setSubscribed(true);
        setEmail('');
      } else if ((result.error || '').toLowerCase().includes('already subscribed')) {
        setError("You're already subscribed with this email. New reviews will arrive in your inbox.");
      } else {
        console.error('Subscription error:', result.error);
        setError("We couldn't subscribe you right now. Check your connection and try again.");
      }
    } catch (err) {
      console.error('Subscription error:', err);
      setError("We couldn't subscribe you right now. Check your connection and try again.");
    } finally {
      setIsSubscribing(false);
    }
  };

  if (subscribed) {
    return (
      <div role="status" className="flex items-start gap-3 rounded-xl bg-sky-400/10 p-4 ring-1 ring-inset ring-sky-400/30">
        <CheckCircle size={20} strokeWidth={1.75} className="mt-0.5 shrink-0 text-sky-400" />
        <div>
          <p className="font-semibold text-slate-50">Successfully subscribed!</p>
          <p className="mt-1 text-sm text-slate-300">You'll receive notifications about new reviews.</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <label htmlFor="subscribe-email" className="field-label">
        Email address
      </label>
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Mail size={18} strokeWidth={1.75} aria-hidden="true" className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            id="subscribe-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            disabled={isSubscribing}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? 'subscribe-error subscribe-help' : 'subscribe-help'}
            className={`field pl-12 ${error ? 'field-invalid' : ''}`}
          />
        </div>
        <button type="submit" disabled={isSubscribing || !email} className="btn-primary">
          {isSubscribing ? (
            <>
              <span className="h-4 w-4 rounded-full border-2 border-slate-950/30 border-t-slate-950 motion-safe:animate-spin" aria-hidden="true" />
              Subscribing...
            </>
          ) : (
            'Subscribe'
          )}
        </button>
      </div>

      {error && (
        <p id="subscribe-error" role="alert" className="field-error">
          {error}
        </p>
      )}

      <p id="subscribe-help" className="mt-3 text-sm text-slate-400">
        We'll only send you notifications about new movie reviews. No spam, ever.
      </p>
    </form>
  );
};

export default SubscriptionForm;